import { Link } from 'react-router';
import type { TargetDTO } from '@devrobot/shared';
import { ExternalLink } from 'lucide-react';
import { CopyButton } from '../../components/CopyButton';
import { KeyValue } from '../../components/KeyValue';
import { RelativeTime } from '../../components/RelativeTime';
import { StatusBadge } from '../../components/StatusBadge';
import { shortDigest } from '../../lib/format';

type ServiceState = TargetDTO['state']['services'][number];

function ServiceTags({ target: t, s }: { target: TargetDTO; s: ServiceState }) {
  return (
    <>
      {t.keepRunningOnStop.includes(s.service) ? (
        <span className="badge badge-ghost badge-xs ml-1" title="Keeps running when the application is stopped">
          kept on stop
        </span>
      ) : null}
      {s.runOnce ? (
        <span className="badge badge-ghost badge-xs ml-1" title="Setup service: runs once and exits">
          setup
        </span>
      ) : null}
    </>
  );
}

const containerValue = (s: ServiceState) => (s.runOnce && s.state === 'exited' && s.exitCode === 0 ? 'completed' : s.state);

export function ServicesTable({ target: t }: { target: TargetDTO }) {
  if (t.state.services.length === 0) {
    return <p className="text-sm text-base-content/60">{t.state.observedAt ? 'No containers observed for this stack.' : 'Services have not been observed yet.'}</p>;
  }
  return (
    <>
      {/* Phones: one stacked row per service instead of a sideways-scrolling table. */}
      <ul className="-mx-1 divide-y divide-hairline md:hidden">
        {t.state.services.map((s) => (
          <li key={s.service} className="space-y-1.5 px-1 py-3 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-center gap-1">
              <span className="font-mono text-sm font-semibold">{s.service}</span>
              <ServiceTags target={t} s={s} />
            </div>
            {s.image ? (
              <div className="truncate font-mono text-xs text-base-content/55" title={s.image}>
                {s.image}
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <StatusBadge kind="container" value={containerValue(s)} stale={t.state.stale} size="xs" />
              <StatusBadge kind="container" value={s.health} stale={t.state.stale} size="xs" />
              <span className="text-base-content/60">
                started <RelativeTime value={s.startedAt} fallback="—" />
              </span>
            </div>
          </li>
        ))}
      </ul>
      <div className="-mx-1 overflow-x-auto max-md:hidden">
        <table className="table table-sm">
          <thead>
            <tr className="text-xs tracking-wide text-base-content/50 uppercase">
              <th>Service</th>
              <th>Container</th>
              <th>Health</th>
              <th>Started</th>
            </tr>
          </thead>
          <tbody>
            {t.state.services.map((s) => (
              <tr key={s.service} className="hover:bg-base-200/50">
                <td>
                  <span className="font-mono text-sm font-medium">{s.service}</span>
                  <ServiceTags target={t} s={s} />
                  {s.image ? (
                    <div className="max-w-56 truncate font-mono text-xs text-base-content/55" title={s.image}>
                      {s.image}
                    </div>
                  ) : null}
                </td>
                <td>
                  <StatusBadge kind="container" value={containerValue(s)} stale={t.state.stale} size="xs" />
                </td>
                <td>
                  <StatusBadge kind="container" value={s.health} stale={t.state.stale} size="xs" />
                </td>
                <td className="text-sm whitespace-nowrap text-base-content/65">
                  <RelativeTime value={s.startedAt} fallback="—" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function CurrentRelease({ target: t }: { target: TargetDTO }) {
  const r = t.currentRelease;
  if (!r) return <p className="text-sm text-base-content/60">Nothing has been deployed to this target yet.</p>;
  const digests = Object.entries(r.imageDigests);
  return (
    <div className="space-y-4">
      <KeyValue
        items={[
          {
            label: 'Commit',
            value: (
              <span className="inline-flex items-center gap-1 font-mono">
                {r.shortSha}
                <CopyButton value={r.commitSha} label="Copy full commit SHA" />
              </span>
            ),
          },
          { label: 'Title', value: r.commitTitle, hidden: !r.commitTitle },
          { label: 'Branch', value: <span className="font-mono">{r.branch}</span> },
          {
            label: 'Pipeline',
            value: r.pipelineUrl ? (
              <a className="link inline-flex items-center gap-1" href={r.pipelineUrl} target="_blank" rel="noreferrer">
                #{r.pipelineId} <ExternalLink className="size-3" aria-hidden="true" />
              </a>
            ) : (
              '—'
            ),
          },
          { label: 'Migrations', value: r.migrations ? <span className="badge badge-soft badge-warning badge-sm">this release ran migrations</span> : 'none' },
          { label: 'Deployed', value: <RelativeTime value={r.createdAt} /> },
          {
            label: 'Last job',
            value: t.lastJob ? (
              <Link className="link" to={`/jobs/${t.lastJob.id}`}>
                {t.lastJob.kind} · {t.lastJob.state}
              </Link>
            ) : null,
            hidden: !t.lastJob,
          },
        ]}
      />
      <div className="rounded-field bg-base-200/60 p-3">
        <p className="mb-1.5 text-xs text-base-content/55">Image digests</p>
        <ul className="space-y-0.5 text-xs">
          {digests.map(([svc, d]) => (
            <li key={svc} className="flex min-w-0 items-center gap-1 font-mono" title={d}>
              <span className="text-base-content/65">{svc}</span> <span className="truncate">{shortDigest(d)}</span>
              <CopyButton value={d} label={`Copy ${svc} digest`} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
